import { Controller } from "@hotwired/stimulus"

// Opens the announcements modal from the header megaphone using a native
// <dialog> (showModal gives focus trapping, focus restoration, Escape-to-close,
// and top-layer rendering for free). The <dialog> shell sits empty in the
// layout; its content is fetched on demand, so announcement copy never renders
// on the hot path. The fetch also marks the announcements as seen server-side;
// the per-item "unseen" dots come back rendered from the pre-open timestamp, so
// they show for this view and clear on the next page load with the badge.
export default class extends Controller {
  static targets = ["badge", "dialog"]
  static values = {url: String}

  // Deep-link support: any admin URL ending in "#announcements" opens the modal
  // on load, so the modal can be shared with a colleague. Clear the hash after
  // so a refresh doesn't reopen it and the URL tidies up.
  connect() {
    if (window.location.hash === "#announcements") {
      this.open()
      history.replaceState(null, "", window.location.pathname + window.location.search)
    }
  }

  async open() {
    const response = await fetch(this.urlValue, {
      // X-Requested-With marks this as the modal fetch (request.xhr? in the
      // controller); plain navigations to the same URL are redirected to the
      // dashboard instead of receiving the bare fragment.
      headers: {"Accept": "text/html", "X-Requested-With": "XMLHttpRequest"},
    })

    // An expired session answers this xhr fetch with a 401. Reloading goes
    // through the full-page signin flow, which returns the user to this page.
    if (response.status === 401) {
      window.location.reload()
      return
    }

    // fetch follows redirects transparently, so a redirect (e.g. to accept
    // terms) arrives here as an ok:true page, and response.redirected is the
    // only trace of it. Follow it for real instead of injecting that page into
    // the dialog.
    if (response.redirected) {
      window.location.assign(response.url)
      return
    }

    // An error page would otherwise be injected into the modal as its content.
    if (!response.ok) return

    this.dialogTarget.innerHTML = await response.text()
    this.dialogTarget.showModal()
    this.hideBadge()
  }

  close() {
    this.dialogTarget.close()
  }

  // A click whose target is the <dialog> itself landed on the backdrop, not the
  // card (which fills the dialog), so dismiss.
  backdropClose(event) {
    if (event.target === this.dialogTarget) this.dialogTarget.close()
  }

  // Drop the fetched content on close so it isn't retained in the DOM.
  reset() {
    this.dialogTarget.innerHTML = ""
  }

  hideBadge() {
    if (this.hasBadgeTarget) this.badgeTarget.remove()
  }
}
